export const PLACE_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const PLACE_DAY_LABELS = {
  mon: { ES: 'Lunes', EN: 'Monday', short: { ES: 'Lun', EN: 'Mon' } },
  tue: { ES: 'Martes', EN: 'Tuesday', short: { ES: 'Mar', EN: 'Tue' } },
  wed: { ES: 'Miércoles', EN: 'Wednesday', short: { ES: 'Mié', EN: 'Wed' } },
  thu: { ES: 'Jueves', EN: 'Thursday', short: { ES: 'Jue', EN: 'Thu' } },
  fri: { ES: 'Viernes', EN: 'Friday', short: { ES: 'Vie', EN: 'Fri' } },
  sat: { ES: 'Sábado', EN: 'Saturday', short: { ES: 'Sáb', EN: 'Sat' } },
  sun: { ES: 'Domingo', EN: 'Sunday', short: { ES: 'Dom', EN: 'Sun' } },
};

export const PLACE_TIMEZONE_OPTIONS = [
  { code: 'America/Tijuana', name: 'America/Tijuana (PT / Baja)' },
  { code: 'America/Mexicali', name: 'America/Mexicali (PT / Baja)' },
  { code: 'America/Ciudad_Juarez', name: 'America/Ciudad_Juarez (MT)' },
  { code: 'America/Hermosillo', name: 'America/Hermosillo (MST)' },
  { code: 'America/Mexico_City', name: 'America/Mexico_City (CT)' },
  { code: 'America/Los_Angeles', name: 'America/Los_Angeles (PT)' },
  { code: 'America/Phoenix', name: 'America/Phoenix (MST)' },
  { code: 'America/Denver', name: 'America/Denver (MT)' },
  { code: 'America/Chicago', name: 'America/Chicago (CT)' },
  { code: 'America/New_York', name: 'America/New_York (ET)' },
];

export function emptyDayHours() {
  return { closed: false, open: '', close: '' };
}

export function defaultHoursSchedule() {
  return {
    timezone: 'America/Tijuana',
    days: {
      mon: emptyDayHours(),
      tue: emptyDayHours(),
      wed: emptyDayHours(),
      thu: emptyDayHours(),
      fri: emptyDayHours(),
      sat: emptyDayHours(),
      sun: emptyDayHours(),
    },
  };
}

export function normalizeHoursSchedule(schedule) {
  const base = defaultHoursSchedule();
  if (!schedule || typeof schedule !== 'object') return base;
  const days = { ...base.days };
  for (const key of PLACE_DAY_KEYS) {
    const src = schedule.days?.[key];
    if (!src || typeof src !== 'object') continue;
    days[key] = {
      closed: Boolean(src.closed),
      open: typeof src.open === 'string' ? src.open : '',
      close: typeof src.close === 'string' ? src.close : '',
    };
  }
  return {
    timezone: schedule.timezone || base.timezone,
    days,
  };
}

const WEEKDAY_TO_KEY = {
  Sun: 'sun',
  Mon: 'mon',
  Tue: 'tue',
  Wed: 'wed',
  Thu: 'thu',
  Fri: 'fri',
  Sat: 'sat',
};

function timeToMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) return null;
  return h * 60 + m;
}

function dayHasWindow(day) {
  if (!day || day.closed) return false;
  return timeToMinutes(day.open) != null && timeToMinutes(day.close) != null;
}

function previousDayKey(key) {
  const idx = PLACE_DAY_KEYS.indexOf(key);
  if (idx < 0) return key;
  return PLACE_DAY_KEYS[(idx + 6) % 7];
}

function getZonedClock(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || 'America/Tijuana',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date);
  const map = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  let hour = Number(map.hour);
  if (hour === 24) hour = 0;
  return {
    dayKey: WEEKDAY_TO_KEY[map.weekday] || 'mon',
    minutes: hour * 60 + Number(map.minute),
  };
}

/** Live open/closed for admin preview */
export function getPlaceOpenStatus(schedule, now = new Date()) {
  const days = schedule?.days;
  if (!days) return null;
  const hasAny =
    PLACE_DAY_KEYS.some((k) => days[k]?.closed) ||
    PLACE_DAY_KEYS.some((k) => dayHasWindow(days[k]));
  if (!hasAny) return null;

  const { dayKey, minutes } = getZonedClock(now, schedule.timezone);
  const today = days[dayKey];
  const yesterday = days[previousDayKey(dayKey)];

  if (dayHasWindow(yesterday)) {
    const openMin = timeToMinutes(yesterday.open);
    const closeMin = timeToMinutes(yesterday.close);
    if (closeMin <= openMin && minutes < closeMin) {
      return { open: true, label: 'Abierto ahora' };
    }
  }

  if (!today || today.closed || !dayHasWindow(today)) {
    return { open: false, label: 'Cerrado' };
  }

  const openMin = timeToMinutes(today.open);
  const closeMin = timeToMinutes(today.close);
  let open = false;
  if (closeMin > openMin) {
    open = minutes >= openMin && minutes < closeMin;
  } else {
    open = minutes >= openMin || minutes < closeMin;
  }
  return { open, label: open ? 'Abierto ahora' : 'Cerrado' };
}

export function formatHoursScheduleLines(schedule) {
  const days = schedule?.days;
  if (!days) return [];
  const lines = [];
  for (const key of PLACE_DAY_KEYS) {
    const day = days[key];
    if (!day) continue;
    const name = PLACE_DAY_LABELS[key]?.short?.ES || key;
    if (day.closed) {
      lines.push(`${name}: Cerrado`);
      continue;
    }
    if (!dayHasWindow(day)) continue;
    lines.push(`${name}: ${day.open} – ${day.close}`);
  }
  return lines;
}
